import { WEB_ICO } from '../shell/icons';
import { useAsync } from '../hooks/useAsync';
import { progressApi } from '../api/progress';
import type { GamificationView } from '../api/progress.types';

interface Props {
  narrow?: boolean;
  gamification: GamificationView | null;
  onStart: () => void;
  onSrsSettings: () => void;
  onOpenKana: () => void;
  onOpenDecks: () => void;
}

function SectionLink({ title, sub, onClick }: { title: string; sub: string; onClick: () => void }) {
  return (
    <button
      onClick={onClick}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 16,
        width: '100%',
        textAlign: 'left',
        background: 'var(--surface-page)',
        border: '1px solid var(--border-default)',
        borderRadius: 'var(--radius-card)',
        padding: 20,
        cursor: 'pointer',
        fontFamily: 'var(--font-vi)',
      }}
    >
      <span style={{ flex: 1, minWidth: 0 }}>
        <span style={{ display: 'block', fontSize: 16, fontWeight: 700, color: 'var(--ink)' }}>{title}</span>
        <span style={{ display: 'block', fontSize: 12.5, color: 'var(--text-secondary)', marginTop: 2 }}>{sub}</span>
      </span>
      <span style={{ display: 'flex', alignItems: 'center', gap: 2, fontSize: 12.5, fontWeight: 600, color: 'var(--cta-default)', flexShrink: 0 }}>
        Tất cả {WEB_ICO.chev('var(--text-secondary)', 18)}
      </span>
    </button>
  );
}

export function WebReviewScreen({ narrow = false, gamification, onStart, onSrsSettings, onOpenKana, onOpenDecks }: Props) {
  const progress = useAsync(() => progressApi.getProgress(), []);
  // Bài hoàn thành = cả track vocab lẫn grammar đều 'done'. Chưa tải xong/lỗi -> 0.
  // Chỉ đếm key Bài 1..25 để tử số không vượt mẫu số.
  const completedCount = Object.entries(progress.data?.branch ?? {}).filter(
    ([bai, b]) => Number(bai) >= 1 && Number(bai) <= 25 && b.vocab.state === 'done' && b.grammar.state === 'done',
  ).length;
  const srsEnabled = gamification?.srsEnabled ?? true;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20, fontFamily: 'var(--font-vi)', maxWidth: 760 }}>
      {srsEnabled ? (
        <div style={{ display: 'grid', gridTemplateColumns: narrow ? '1fr' : '1fr', gap: 12 }}>
          <div style={{ background: 'var(--surface-page)', border: '1px solid var(--border-default)', borderRadius: 'var(--radius-card)', padding: 24 }}>
            <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 16 }}>
              <div>
                <div style={{ display: 'flex', alignItems: 'baseline', gap: 10 }}>
                  <span style={{ fontSize: 48, fontWeight: 700, color: 'var(--ink)', lineHeight: 1 }}>{gamification?.srsDueCount ?? 0}</span>
                  <span style={{ fontSize: 15, fontWeight: 600, color: 'var(--text-secondary)' }}>thẻ đến hạn</span>
                </div>
                <div style={{ fontSize: 13.5, color: 'var(--text-secondary)', marginTop: 6 }}>
                  {`Mỗi lần ôn tối đa ${gamification?.srsSessionSize ?? 20} thẻ`}
                </div>
              </div>
              <button
                onClick={onSrsSettings}
                aria-label="Cài đặt SRS"
                title="Cài đặt SRS"
                style={{
                  width: 38,
                  height: 38,
                  borderRadius: 12,
                  border: '1px solid var(--border-default)',
                  background: 'var(--surface-page)',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                }}
              >
                {WEB_ICO.gear('var(--ink)', 18)}
              </button>
            </div>
            <button
              onClick={onStart}
              style={{
                width: '100%',
                marginTop: 20,
                height: 52,
                borderRadius: 'var(--radius-control)',
                background: 'var(--cta-default)',
                color: '#fff',
                border: 'none',
                cursor: 'pointer',
                fontFamily: 'var(--font-vi)',
                fontWeight: 700,
                fontSize: 16,
              }}
            >
              Bắt đầu ôn
            </button>
          </div>
        </div>
      ) : (
        <div style={{ background: 'var(--surface-page)', border: '1px solid var(--border-default)', borderRadius: 'var(--radius-card)', padding: 24 }}>
          <div style={{ fontSize: 16, fontWeight: 700, color: 'var(--ink)' }}>SRS đang tắt</div>
          <div style={{ fontSize: 13.5, color: 'var(--text-secondary)', marginTop: 4 }}>
            Flashcard chỉ duyệt tự do, không lên lịch ôn lại. Lịch sử ôn tập trước đó vẫn được giữ.
          </div>
          <button
            onClick={onSrsSettings}
            style={{
              width: '100%',
              marginTop: 16,
              height: 48,
              borderRadius: 'var(--radius-control)',
              background: 'var(--surface-tint)',
              border: '1px solid var(--border-default)',
              color: 'var(--ink)',
              cursor: 'pointer',
              fontFamily: 'var(--font-vi)',
              fontWeight: 600,
              fontSize: 15,
            }}
          >
            Bật SRS trong Cài đặt
          </button>
        </div>
      )}

      <SectionLink title="Thư viện chữ Nhật" sub="Hiragana · Katakana · Bộ thủ" onClick={onOpenKana} />
      <SectionLink title="Thư viện Minna" sub={`${completedCount}/25 bài hoàn thành · tự học flashcard`} onClick={onOpenDecks} />
    </div>
  );
}
